import { Flex, Input, Text } from '@byecode/ui'
import type { AnchorConfigProtocol } from '@lighthouse/core'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback } from 'react'
import { useUpdateEffect } from 'react-use'
import { useImmer } from 'use-immer'

import { transform2Number } from '@/utils/number'

type OffsetState = Required<AnchorConfigProtocol>

const initOffsetValue: OffsetState['offset'] = { x: 0, y: 0 }

interface Props {
    value?: AnchorConfigProtocol['offset']
    onChange: (val: OffsetState['offset']) => void
}

export const OffsetInput: React.FC<Props> = ({ value, onChange }) => {
    const [offsetValue, setOffsetValue] = useImmer<OffsetState['offset']>(value || initOffsetValue)

    const { x = 0, y = 0 } = offsetValue

    useUpdateEffect(() => {
        if (isDeepEqual(value, offsetValue)) {
            return
        }
        setOffsetValue(value || initOffsetValue)
    }, [value])

    const handleOffsetChange = useCallback(
        (value: string, key: keyof OffsetState['offset']) => {
            const num = value ? transform2Number(value) : 0
            setOffsetValue(draft => {
                draft[key] = num
            })
        },
        [setOffsetValue]
    )

    const handleSubmit = useCallback(() => {
        onChange(offsetValue)
    }, [offsetValue, onChange])

    return (
        <Flex gap={8} styles={{ root: { width: 180 } }}>
            <Input
                prefix={
                    <Text pb={2} size={14}>
                        x
                    </Text>
                }
                placeholder=""
                onFocus={e => e.currentTarget.select()}
                value={x}
                type="number"
                style={{ width: 86 }}
                onChange={ev => handleOffsetChange(ev.target.value, 'x')}
                onKeyDownCapture={ev => {
                    if (ev.key === 'Enter') {
                        ev.currentTarget.blur()
                    }
                }}
                onBlur={handleSubmit}
            />
            <Input
                prefix={
                    <Text pb={2} size={14}>
                        y
                    </Text>
                }
                type="number"
                placeholder=""
                onFocus={e => e.currentTarget.select()}
                value={y}
                style={{ width: 86 }}
                onChange={ev => handleOffsetChange(ev.target.value, 'y')}
                onKeyDownCapture={ev => {
                    if (ev.key === 'Enter') {
                        ev.currentTarget.blur()
                    }
                }}
                onBlur={handleSubmit}
            />
        </Flex>
    )
}
